import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertCircle, X, CheckCircle, Clock, ExternalLink, 
  ArrowRight, Calendar, Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ApplicationStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobTitle: string;
  applicationStatus: string;
  interviewUrl?: string;
  appliedDate?: string;
  interviewStatus?: 'in_progress' | 'completed' | 'not_started';
}

const ApplicationStatusModal: React.FC<ApplicationStatusModalProps> = ({
  isOpen,
  onClose,
  jobTitle,
  applicationStatus,
  interviewUrl,
  appliedDate,
  interviewStatus = 'not_started'
}) => {
  const navigate = useNavigate();

  const getStatusInfo = () => {
    switch (applicationStatus.toLowerCase()) {
      case 'pending':
        if (interviewStatus === 'in_progress') {
          return {
            icon: <Clock className="w-6 h-6 text-blue-400" />,
            title: 'Interview In Progress',
            message: 'You applied to this position before but didn\'t complete the interview. Would you like to continue?',
            actionText: 'Continue Interview',
            actionColor: 'bg-blue-600 hover:bg-blue-700',
            showDashboardButton: true
          };
        } else if (interviewStatus === 'completed') {
          return {
            icon: <CheckCircle className="w-6 h-6 text-green-400" />,
            title: 'Already Applied',
            message: 'You have completed the interview for this position before. Go to dashboard to check your application status.',
            actionText: 'Go to Dashboard',
            actionColor: 'bg-green-600 hover:bg-green-700',
            showDashboardButton: true
          };
        } else {
          return {
            icon: <AlertCircle className="w-6 h-6 text-orange-400" />,
            title: 'Already Applied',
            message: 'You have already applied for this position before. Go to dashboard to check your status.',
            actionText: 'Go to Dashboard',
            actionColor: 'bg-orange-600 hover:bg-orange-700',
            showDashboardButton: true
          };
        }
      
      case 'reviewed':
      case 'interviewed':
        return {
          icon: <CheckCircle className="w-6 h-6 text-blue-400" />,
          title: 'Application Under Review',
          message: 'Your application is being reviewed by our team.',
          actionText: 'View Application',
          actionColor: 'bg-blue-600 hover:bg-blue-700',
          showDashboardButton: true
        };
      
      case 'accepted':
        return {
          icon: <CheckCircle className="w-6 h-6 text-green-400" />,
          title: 'Congratulations! 🎉',
          message: 'Your application has been accepted!',
          actionText: 'View Application',
          actionColor: 'bg-green-600 hover:bg-green-700',
          showDashboardButton: true
        };
      
      case 'rejected':
        return {
          icon: <X className="w-6 h-6 text-red-400" />,
          title: 'Application Not Selected',
          message: 'Unfortunately, your application was not selected for this position.',
          actionText: 'View Application',
          actionColor: 'bg-gray-600 hover:bg-gray-700',
          showDashboardButton: true
        };
      
      default:
        return {
          icon: <AlertCircle className="w-6 h-6 text-gray-400" />,
          title: 'Application Exists',
          message: 'You have already applied for this position.',
          actionText: 'View Application',
          actionColor: 'bg-gray-600 hover:bg-gray-700',
          showDashboardButton: true
        };
    }
  };

  const statusInfo = getStatusInfo();

  const handlePrimaryAction = () => {
    if (interviewUrl && interviewStatus === 'in_progress') {
      // Navigate to interview
      try {
        navigate(interviewUrl);
        onClose();
      } catch (error) {
        // Fallback to window.location if navigate fails
        window.location.href = interviewUrl;
      }
    } else {
      // Navigate to dashboard
      navigate('/dashboard');
      onClose();
    }
  };

  const handleViewDashboard = () => {
    navigate('/dashboard');
    onClose();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 20 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-gray-900 rounded-2xl border border-gray-800 shadow-2xl max-w-md w-full">
              {/* Header */}
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center">
                      {statusInfo.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{statusInfo.title}</h3>
                      <p className="text-gray-400 text-sm">Job Application Status</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Job Information */}
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-4 h-4 text-purple-400" />
                    <span className="text-white font-medium">{jobTitle}</span>
                  </div>
                  {appliedDate && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>Applied on {formatDate(appliedDate)}</span>
                    </div>
                  )}
                </div>

                {/* Status Message */}
                <div className="text-center">
                  <p className="text-gray-300 leading-relaxed">
                    {statusInfo.message}
                  </p>
                </div>

                {/* Interview Status Indicator */}
                {interviewStatus === 'in_progress' && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-blue-200 text-sm text-center">
                      💡 <strong>Tip:</strong> Complete your interview to finalize your application
                    </p>
                  </div>
                )}

                {interviewStatus === 'completed' && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <p className="text-green-200 text-sm text-center">
                      ✅ <strong>Interview Complete:</strong> Your application is now under review
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-800 space-y-3">
                {/* Primary Action Button */}
                <button
                  onClick={handlePrimaryAction}
                  className={`w-full px-4 py-3 ${statusInfo.actionColor} text-white rounded-lg transition font-medium flex items-center justify-center gap-2`}
                >
                  {interviewStatus === 'in_progress' ? (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      {statusInfo.actionText}
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      {statusInfo.actionText}
                    </>
                  )}
                </button>

                {/* Secondary Action - Go to Dashboard (only if not the primary action) */}
                {interviewStatus === 'in_progress' && (
                  <button
                    onClick={handleViewDashboard}
                    className="w-full px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition font-medium"
                  >
                    View Dashboard
                  </button>
                )}

                {/* Cancel Button */}
                <button
                  onClick={onClose}
                  className="w-full px-4 py-3 bg-transparent border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ApplicationStatusModal;